import React, { useState, useRef, useEffect } from "react";
import { Paperclip, X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../css/AddTemplate.css";
import axios from "axios";

const AddTemplate = ({ templateData, isOpen, onClose, onSaved, activecompetitionId }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    from_name: "",
    from_email: "",
    subject: "",
    copy_to: false,
    body: "",
    files: [],
    competition_id: "",
  });

  const [attachments, setAttachments] = useState([]);

  // -----------------------------
  // ⭐ ADDED FULLNAME PLACEHOLDER
  // -----------------------------
  const placeholders = [
    "{{fullname}}", // <-- merged placeholder
    "{{name}}",
    "{{mname}}",
    "{{lname}}",
    "{{email}}",
    "{{address1}}",
    "{{address2}}",
    "{{phone1}}",
    "{{phone2}}",
    "{{username}}",
  ];

  // initialize modal
  useEffect(() => {
    if (isOpen) {
      const initData = {
        name: templateData?.name || "",
        from_name: templateData?.from_name || "",
        from_email: templateData?.from_email || "",
        subject: templateData?.subject || "",
        copy_to: templateData?.copy_to || false,
        body: templateData?.body || "",
        files: templateData?.attachments || [],
        competition_id: templateData?.competition_id || activecompetitionId || "",
      };

      setFormData(initData);
      setAttachments(templateData?.attachments || []);
    }
  }, [isOpen, templateData, activecompetitionId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBodyChange = (value) => {
    setFormData((prev) => ({ ...prev, body: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  // Insert placeholder into Quill
  const insertPlaceholder = (placeholder) => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    if (range) {
      quill.insertText(range.index, placeholder);
      quill.setSelection(range.index + placeholder.length, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();

      payload.append("name", formData.name);
      payload.append("from_name", formData.from_name);
      payload.append("from_email", formData.from_email);
      payload.append("subject", formData.subject);
      payload.append("copy_to", formData.copy_to);
      payload.append("body", formData.body);

      if (formData.competition_id)
        payload.append("competition_id", formData.competition_id);
      else if (activecompetitionId)
        payload.append("competition_id", activecompetitionId);

      attachments.forEach((file) => payload.append("files", file));

      if (templateData?._id) {
        await axios.put(`${API_URL}/ems/templates/${templateData._id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API_URL}/ems/templates`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("Error saving template:", err.response?.data || err.message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="modal-header">
            <h6 className="mb-2">{templateData?._id ? "Edit Template" : "New Template"}</h6>
            <button className="close-btn" type="button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          <div className="add-template modal-body">
            <input type="text" name="name" placeholder="Template Name" value={formData.name} onChange={handleChange} required />

            <input type="text" name="from_name" placeholder="From Name" value={formData.from_name} onChange={handleChange} required />

            <input type="email" name="from_email" placeholder="From Email" value={formData.from_email} onChange={handleChange} required />

            <input type="text" name="subject" placeholder="Subject" value={formData.subject} onChange={handleChange} required />

            <div className="placeholders-info">
              {placeholders.map((ph) => (
                <span key={ph} onClick={() => insertPlaceholder(ph)} className="clickable-placeholder">
                  {ph}
                </span>
              ))}
            </div>

            <div className="form-group">
              <ReactQuill
                ref={quillRef}
                value={formData.body}
                onChange={handleBodyChange}
                placeholder="Write your template content..."
              />
            </div>

            <div className="attachments">
              <button type="button" onClick={() => fileInputRef.current?.click()}>
                <Paperclip size={16} /> Attach
              </button>

              <input type="file" multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />

              <ul>
                {attachments.map((file, idx) => (
                  <li key={idx}>
                    {file.name || file.filename}
                    <button type="button" onClick={() => removeAttachment(idx)}>
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="modal-footer">
            <button type="submit" className="btn-warning">Save</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTemplate;
